/**
 * Utility functions to convert ProductDetail to API update payload format
 * Handles the differences between GET and PUT API structures
 */

import { ProductDetail } from '../types/ProductTypes';

/**
 * Product Update Response Interface
 * Response from the PUT API endpoint
 */
export interface ProductUpdateResponse {
    success: boolean;
    message?: string;
    data?: any;
    errors?: string[];
}

/**
 * Convert ProductDetail to API update payload format
 * Handles structural differences between GET response and PUT request
 */
export function convertProductDetailToUpdatePayload(product: ProductDetail, updates?: Partial<ProductDetail>): any {
    // Merge any updates with the existing product
    const merged = { ...product, ...updates };

    console.log('[convertProductDetailToUpdatePayload] === DEBUG ===');
    console.log('[convertProductDetailToUpdatePayload] updates?.category:', updates?.category);
    console.log('[convertProductDetailToUpdatePayload] updates?.category?.regulations?.ibcRegulation?.specificAndOperationalRequirements:',
        updates?.category?.regulations?.ibcRegulation?.specificAndOperationalRequirements);
    console.log('[convertProductDetailToUpdatePayload] merged.category?.regulations?.ibcRegulation?.specificAndOperationalRequirements:',
        merged.category?.regulations?.ibcRegulation?.specificAndOperationalRequirements);
    console.log('[convertProductDetailToUpdatePayload] === END DEBUG ===');

    return {
        LastUpdatedUtc: new Date().toISOString(),
        Id: merged.id,
        CategoryId: merged.categoryId || merged.category?.id || 0,
        TradeName: merged.tradeName || '',
        MainHazards: merged.mainHazards || '',
        IsNonCargo: merged.isNonCargo,
        IsActive: merged.isActive,
        IsApproved: merged.isApproved,

        // Convert Synonyms: handle both string[] and object[] formats
        Synonyms:
            merged.synonyms?.map((syn: any) => {
                // If syn is already an object with synonymName, use it
                if (typeof syn === 'object' && syn !== null && 'synonymName' in syn) {
                    return {
                        Id: syn.id || 0,
                        SynonymName: syn.synonymName,
                    };
                }
                // If syn is a string, convert it
                return {
                    Id: 0, // Backend will handle ID assignment
                    SynonymName: String(syn),
                };
            }) || [],

        // Convert Procedures
        Procedures:
            merged.procedures?.map((proc: any) => ({
                Id: proc.id,
                LastUpdatedUtc: proc.lastUpdatedUtc,
                Title: proc.title,
                Description: proc.description,
                Comments: proc.comments || null,
                FilePath: proc.filePath || null,
                Category: proc.category,
                Score: proc.score,
            })) || [],

        // Convert Coating Compatibilities
        CoatingCompatibilities:
            merged.coatingCompatibilities?.map((cc: any) => ({
                Id: cc.id,
                Coating: cc.coating,
                Compatibility: cc.compatibility,
                MaxTemperature: cc.maxTemperature,
                CoatingNotes: cc.coatingNotes || [],
                LastUpdatedUtc: cc.lastUpdatedUtc,
            })) || [],

        // External IDs
        ExternalIds:
            merged.externalIds?.map((extId: any) => ({
                Id: extId.id,
                Value: extId.value,
                Source: extId.source,
                LastUpdatedUtc: extId.lastUpdatedUtc,
            })) || [],

        // Comments
        Comments:
            merged.comments?.map((comment: any) => ({
                Id: comment.id,
                Text: comment.text,
                Category: comment.category,
                Visibility: comment.visibility,
                LastUpdatedUtc: comment.lastUpdatedUtc,
            })) || [],

        // Stowage Rules
        StowageRules: merged.stowageRules || [],

        // Temperature Requirements
        TemperatureRequirements: merged.temperatureRequirements,

        // Physical Data - convert TemperatureValue objects to strings if needed
        PhysicalData: merged.physicalData ? convertPhysicalDataForUpdate(merged.physicalData) : null,

        // Gas Physical Data
        GasPhysicalData: merged.gasPhysicalData || null,

        // Category - simplified structure for update
        Category: merged.category
            ? {
                Id: merged.category.id,
                Name: merged.category.name,
                IsStaticAccumulator: merged.category.isStaticAccumulator,
                ThresholdLimitValueTimeWeightedAverage: merged.category.thresholdLimitValueTimeWeightedAverage,
                OdorThreshold: merged.category.odorThreshold,
                ExternalIds: merged.category.externalIds || [],
                Synonyms: merged.category.synonyms || [],
                Comments: merged.category.comments || [],
                DraegerTubes: merged.category.draegerTubes || [],
                StowageRules: merged.category.stowageRules || [],
                Regulations: merged.category.regulations ? {
                    MarpolPollutionCategory: merged.category.regulations.marpolPollutionCategory,
                    MarpolRegulation: merged.category.regulations.marpolRegulation,
                    IbcRegulation: merged.category.regulations.ibcRegulation ? {
                        Id: merged.category.regulations.ibcRegulation.id,
                        Hazards: merged.category.regulations.ibcRegulation.hazards,
                        ShipType: merged.category.regulations.ibcRegulation.shipType,
                        TankType: merged.category.regulations.ibcRegulation.tankType,
                        TankVents: merged.category.regulations.ibcRegulation.tankVents,
                        TankEnvironmentalControl: merged.category.regulations.ibcRegulation.tankEnvironmentalControl,
                        Gauging: merged.category.regulations.ibcRegulation.gauging,
                        VapourDetection: merged.category.regulations.ibcRegulation.vapourDetection,
                        FireProtection: merged.category.regulations.ibcRegulation.fireProtection,
                        RequiresEmergencyEquipment: merged.category.regulations.ibcRegulation.requiresEmergencyEquipment,
                        SpecificAndOperationalRequirements: merged.category.regulations.ibcRegulation.specificAndOperationalRequirements?.map((req: any) => ({
                            Id: req.id,
                            ParagraphNumber: req.paragraphNumber,
                            ParagraphHeading: req.paragraphHeading,
                            Text: req.text,
                            LastUpdatedUtc: req.lastUpdatedUtc,
                        })) || [],
                        LastUpdatedUtc: merged.category.regulations.ibcRegulation.lastUpdatedUtc,
                    } : null,
                    IgcRegulation: merged.category.regulations.igcRegulation ? {
                        Id: merged.category.regulations.igcRegulation.id,
                        SpecificAndOperationalRequirements: merged.category.regulations.igcRegulation.specificAndOperationalRequirements?.map((req: any) => ({
                            Id: req.id,
                            ParagraphNumber: req.paragraphNumber,
                            ParagraphHeading: req.paragraphHeading,
                            Text: req.text,
                            LastUpdatedUtc: req.lastUpdatedUtc,
                        })) || [],
                        LastUpdatedUtc: merged.category.regulations.igcRegulation.lastUpdatedUtc,
                    } : null,
                } : undefined,
                PhysicalData: merged.category.physicalData || {},
                SafetyEquipment: merged.category.safetyEquipment ? {
                    Id: merged.category.safetyEquipment.id,
                    NormalExposureLevel: merged.category.safetyEquipment.normalExposureLevel,
                    HighExposureLevel: merged.category.safetyEquipment.highExposureLevel,
                    ExtremeExposureLevel: merged.category.safetyEquipment.extremeExposureLevel,
                    SafetyHazards: merged.category.safetyEquipment.safetyHazards?.map((h: any) => ({
                        Id: h.id,
                        HazardCode: h.hazardCode,
                        HazardDescription: h.hazardDescription,
                        NormalExposureLevel: h.normalExposureLevel,
                        HighExposureLevel: h.highExposureLevel,
                        ExtremeExposureLevel: h.extremeExposureLevel,
                        WarningLevel: h.warningLevel,
                        WarningImage: h.warningImage,
                        Category: h.category,
                        HealthPropertiesScore: h.healthPropertiesScore,
                        PhysicalPropertiesScore: h.physicalPropertiesScore,
                        EnvironmentPropertiesScore: h.environmentPropertiesScore,
                        IsActive: h.isActive,
                        LastUpdatedUtc: h.lastUpdatedUtc,
                    })) || [],
                    LastUpdatedUtc: merged.category.safetyEquipment.lastUpdatedUtc,
                } : undefined,
                LastUpdatedUtc: merged.category.lastUpdatedUtc,
                IsInhibited: merged.category.isInhibited,
                ShallNotBeExposedToExcessiveHeat: merged.category.shallNotBeExposedToExcessiveHeat,
            }
            : undefined,

        // Charterer - backend expects { Id, ExternalId, Name, Code }
        Charterer: merged.charterer
            ? {
                Id: merged.charterer.id,
                ExternalId: merged.charterer.externalId,
                Name: merged.charterer.name,
                Code: merged.charterer.code,
            }
            : null,

        // Regulations - simplified for update
        Regulations: merged.regulations
            ? {
                TripartiteAgreement: merged.regulations.tripartiteAgreement || null,
                USCGGroup: merged.regulations.uscgGroup ? { Id: merged.regulations.uscgGroup.id } : null,
                IMDGCodeClass: merged.regulations.imdgCodeClass ? { Id: merged.regulations.imdgCodeClass.id } : null,
                PersistentFloater: merged.regulations.persistentFloater,
                IsOpa90: merged.regulations.isOpa90,
                NumberOfLastCargoesForEpoxyTanksFosfaBan: merged.regulations.numberOfLastCargoesForEpoxyTanksFosfaBan,
                FosfaAllowance: merged.regulations.fosfaAllowance,
                EuAllowance: merged.regulations.euAllowance,
                Niop1Allowance: merged.regulations.niop1Allowance,
                Niop2Allowance: merged.regulations.niop2Allowance,
            }
            : undefined,

        // Material Safety Data Sheets
        MaterialSafetyDataSheet: merged.materialSafetyDataSheet || null,
        MaterialSafetyDataSheets: merged.materialSafetyDataSheets || [],

        // Product Documents
        ProductDocuments: merged.productDocuments || [],

        // Draeger Tubes
        DraegerTubes: merged.draegerTubes || [],
    };
}

/**
 * Convert PhysicalData for update API
 * Backend expects Weight and Temperature objects, not strings
 */
function convertPhysicalDataForUpdate(physicalData: any): any {
    // Pass through as-is - backend expects Weight and Temperature objects
    // Do NOT convert to strings as the API expects typed objects
    return {
        ...physicalData,
        // Keep object structures intact for Weight and Temperature types
        LitreWeight: physicalData.LitreWeight || null,
        LitreWeightReferenceTemperature: physicalData.LitreWeightReferenceTemperature || null,
        TemperatureToAchieve50mPas: physicalData.TemperatureToAchieve50mPas || null,
        ViscosityReferenceTemperature: physicalData.ViscosityReferenceTemperature || null,
        VapourPressureReferenceTemperature: physicalData.VapourPressureReferenceTemperature || null,
    };
}

/**
 * Helper function to update specific fields in a ProductDetail
 * Returns a new object ready for API submission
 */
export function updateProductFields(product: ProductDetail, fieldUpdates: Partial<ProductDetail>): any {
    return convertProductDetailToUpdatePayload(product, fieldUpdates);
}

/**
 * Helper to build update payload with changed fields merged into full product
 * API requires the complete product object, not just changed fields
 */
export function buildMinimalUpdatePayload(
    product: ProductDetail,
    changedFields: {
        tradeName?: string;
        synonyms?: string[];
        casNumber?: string;
        description?: string;
        warning?: string;
        chartererName?: string;
        categoryName?: string;
        materialSafetyDataSheets?: { id: number; filePath: string; lastUpdatedUtc: string; comments: string | null; receivedDate: string | null; receivedFrom: string | null; date: string | null; version: string | null }[];
        productDocuments?: { id: number; filePath: string; lastUpdatedUtc: string }[];
        additionalComments?: { id: number; text: string; category: number; visibility: number; lastUpdatedUtc: string }[];
        coatingCompatibilities?: { id: number; coating: any; compatibility: number; maxTemperature: number | null; coatingNotes: any[]; lastUpdatedUtc: string }[];
        procedures?: { id: number; lastUpdatedUtc: string; title: string; description: string; comments: string | null; filePath: string | null; category: number; score: number }[];
        externalIds?: { id: number; value: string; source: string; lastUpdatedUtc: string }[];
        stowageRules?: { id: number; name: string; lastUpdatedUtc?: string }[];
        temperatureRequirements?: any;
        regulations?: any;
        category?: any;
    }
): any {
    // Start with the full product, converted to API format
    // Using 'any' because we're building an API payload with PascalCase properties
    const updates: any = {};

    // Apply changed fields to the updates object (using camelCase to match ProductDetail format)
    if (changedFields.tradeName !== undefined) {
        updates.tradeName = changedFields.tradeName;
    }

    if (changedFields.synonyms !== undefined) {
        updates.synonyms = changedFields.synonyms;
    }

    // Handle CAS Number (stored in ExternalIds array)
    if (changedFields.casNumber !== undefined) {
        const casExtId = product.externalIds?.find((extId) => extId.source === 'CAS');
        const otherExtIds = product.externalIds?.filter((extId) => extId.source !== 'CAS') || [];

        // Only add CAS external ID if it has a value
        if (changedFields.casNumber && changedFields.casNumber.trim() !== '') {
            updates.externalIds = [
                ...otherExtIds,
                {
                    id: casExtId?.id || 0,
                    value: changedFields.casNumber,
                    source: 'CAS',
                    lastUpdatedUtc: new Date().toISOString(),
                },
            ];
        } else {
            // If CAS number is empty, just keep other external IDs
            updates.externalIds = otherExtIds;
        }
    }

    // Handle Description, Warning, and Additional Comments (stored in Comments array)
    if (changedFields.description !== undefined || changedFields.warning !== undefined || changedFields.additionalComments !== undefined) {
        const descComment = product.comments?.find((c) => c.category === 4);
        const warnComment = product.comments?.find((c) => c.category === 5);

        // Get categories that will be replaced by additionalComments
        const additionalCategories = new Set(changedFields.additionalComments?.map((c) => c.category) || []);

        // Get other comments (excluding description, warning, and those being replaced by additionalComments)
        const otherComments = product.comments?.filter((c) =>
            c.category !== 4 && c.category !== 5 && !additionalCategories.has(c.category)
        ) || [];

        // Collect updated comments (using camelCase to match ProductDetail format)
        const updatedComments: any[] = otherComments.map((c) => ({
            id: c.id,
            text: c.text,
            category: c.category,
            visibility: c.visibility,
            lastUpdatedUtc: c.lastUpdatedUtc,
        }));

        // Add/update description comment (Category 4)
        if (changedFields.description !== undefined) {
            updatedComments.push({
                id: descComment?.id || 0,
                text: changedFields.description,
                category: 4,
                visibility: 1,
                lastUpdatedUtc: new Date().toISOString(),
            });
        } else if (descComment) {
            updatedComments.push({
                id: descComment.id,
                text: descComment.text,
                category: descComment.category,
                visibility: descComment.visibility,
                lastUpdatedUtc: descComment.lastUpdatedUtc,
            });
        }

        // Add/update warning comment (Category 5)
        if (changedFields.warning !== undefined && changedFields.warning) {
            updatedComments.push({
                id: warnComment?.id || 0,
                text: changedFields.warning,
                category: 5,
                visibility: 1,
                lastUpdatedUtc: new Date().toISOString(),
            });
        } else if (warnComment) {
            updatedComments.push({
                id: warnComment.id,
                text: warnComment.text,
                category: warnComment.category,
                visibility: warnComment.visibility,
                lastUpdatedUtc: warnComment.lastUpdatedUtc,
            });
        }

        // Add additional comments (e.g., coating comments from other sections)
        if (changedFields.additionalComments) {
            changedFields.additionalComments.forEach((c) => {
                updatedComments.push({
                    id: c.id,
                    text: c.text,
                    category: c.category,
                    visibility: c.visibility,
                    lastUpdatedUtc: c.lastUpdatedUtc,
                });
            });
        }

        updates.comments = updatedComments;
    }

    // Handle Charterer name change - use camelCase to match ProductDetail type
    if (changedFields.chartererName !== undefined && product.charterer) {
        updates.charterer = {
            ...product.charterer,
            name: changedFields.chartererName,
        };
    }

    // Handle Category name change - use camelCase to match ProductDetail type
    if (changedFields.categoryName !== undefined && product.category) {
        updates.category = {
            ...product.category,
            name: changedFields.categoryName,
        };
    }

    // Handle MaterialSafetyDataSheets update
    if (changedFields.materialSafetyDataSheets !== undefined) {
        updates.materialSafetyDataSheets = changedFields.materialSafetyDataSheets.map((msds) => ({
            id: msds.id,
            filePath: msds.filePath,
            lastUpdatedUtc: msds.lastUpdatedUtc,
            comments: msds.comments,
            receivedDate: msds.receivedDate,
            receivedFrom: msds.receivedFrom,
            date: msds.date,
            version: msds.version,
        }));
    }

    // Handle ProductDocuments update
    if (changedFields.productDocuments !== undefined) {
        updates.productDocuments = changedFields.productDocuments.map((doc) => ({
            id: doc.id,
            filePath: doc.filePath,
            lastUpdatedUtc: doc.lastUpdatedUtc,
        }));
    }

    // Handle CoatingCompatibilities update
    if (changedFields.coatingCompatibilities !== undefined) {
        updates.coatingCompatibilities = changedFields.coatingCompatibilities.map((cc) => ({
            id: cc.id,
            coating: cc.coating,
            compatibility: cc.compatibility,
            maxTemperature: cc.maxTemperature,
            coatingNotes: cc.coatingNotes || [],
            lastUpdatedUtc: cc.lastUpdatedUtc,
        }));
    }

    // Handle Procedures update
    if (changedFields.procedures !== undefined) {
        updates.procedures = changedFields.procedures.map((proc) => ({
            id: proc.id,
            lastUpdatedUtc: proc.lastUpdatedUtc,
            title: proc.title,
            description: proc.description,
            comments: proc.comments,
            filePath: proc.filePath,
            category: proc.category,
            score: proc.score,
        }));
    }

    // Handle StowageRules update
    if (changedFields.stowageRules !== undefined) {
        updates.stowageRules = changedFields.stowageRules.map((rule) => ({
            id: rule.id,
            name: rule.name,
            lastUpdatedUtc: rule.lastUpdatedUtc || new Date().toISOString(),
        }));
    }

    // Handle ExternalIds update
    if (changedFields.externalIds !== undefined) {
        updates.externalIds = changedFields.externalIds.map((extId) => ({
            id: extId.id && extId.id > 0 ? extId.id : 0,
            value: extId.value,
            source: extId.source,
            lastUpdatedUtc: extId.lastUpdatedUtc || new Date().toISOString(),
        }));
    }

    // Handle TemperatureRequirements update
    if (changedFields.temperatureRequirements !== undefined) {
        updates.temperatureRequirements = changedFields.temperatureRequirements;
    }

    // Handle Regulations update (e.g., Fosfa value)
    if (changedFields.regulations !== undefined) {
        updates.regulations = {
            ...product.regulations,
            ...changedFields.regulations,
        };
    }

    // Handle Category update (e.g., IBC requirements)
    if (changedFields.category !== undefined) {
        console.log('[buildMinimalUpdatePayload] === CATEGORY DEBUG ===');
        console.log('[buildMinimalUpdatePayload] changedFields.category:', changedFields.category);
        console.log('[buildMinimalUpdatePayload] changedFields.category.regulations:', changedFields.category?.regulations);
        console.log('[buildMinimalUpdatePayload] changedFields.category.regulations.ibcRegulation:', changedFields.category?.regulations?.ibcRegulation);
        console.log('[buildMinimalUpdatePayload] changedFields.category.regulations.ibcRegulation.specificAndOperationalRequirements:',
            changedFields.category?.regulations?.ibcRegulation?.specificAndOperationalRequirements);

        updates.category = {
            ...product.category,
            ...changedFields.category,
            // Deep merge regulations if both exist
            regulations: changedFields.category?.regulations ? {
                ...product.category?.regulations,
                ...changedFields.category.regulations,
                // Deep merge ibcRegulation if it exists
                ibcRegulation: changedFields.category.regulations?.ibcRegulation ? {
                    ...product.category?.regulations?.ibcRegulation,
                    ...changedFields.category.regulations.ibcRegulation,
                } : product.category?.regulations?.ibcRegulation,
            } : product.category?.regulations,
            // Deep merge safetyEquipment if changed
            safetyEquipment: changedFields.category?.safetyEquipment ? {
                ...product.category?.safetyEquipment,
                ...changedFields.category.safetyEquipment,
            } : product.category?.safetyEquipment,
        };

        console.log('[buildMinimalUpdatePayload] updates.category after merge:', updates.category);
        console.log('[buildMinimalUpdatePayload] updates.category.safetyEquipment:', updates.category?.safetyEquipment);
        console.log('[buildMinimalUpdatePayload] updates.category.regulations.ibcRegulation:', updates.category?.regulations?.ibcRegulation);
        console.log('[buildMinimalUpdatePayload] updates.category.regulations.ibcRegulation.specificAndOperationalRequirements:',
            updates.category?.regulations?.ibcRegulation?.specificAndOperationalRequirements);
        console.log('[buildMinimalUpdatePayload] === END CATEGORY DEBUG ===');
    }

    // Use the full converter with the updates merged in
    return convertProductDetailToUpdatePayload(product, updates);
}
